import { useRef } from 'react';
import type { Issue, Rocket } from '@openrocket-web/physics';
import { KIND_LABEL, allComponents, type ComponentKind } from '../state/editor';
import type { Action } from '../state/app';
import { Icon, type IconName } from './icons';

interface TreeProps {
  readonly rocket: Rocket;
  readonly selectedId: string | undefined;
  readonly issues: readonly Issue[];
  readonly dispatch: (a: Action) => void;
  /** Doble clic o Intro sobre un componente: lleva el foco a sus propiedades. */
  readonly onEdit: () => void;
}

interface ActionsProps {
  readonly rocket: Rocket;
  readonly selectedId: string | undefined;
  readonly dispatch: (a: Action) => void;
  readonly onEdit: () => void;
}

export function selection(rocket: Rocket, selectedId: string | undefined) {
  const entries = allComponents(rocket);
  const selected = entries.find((e) => e.component.id === selectedId);
  const isStack = selected !== undefined && selected.parentId === undefined;
  const stackIndex = isStack ? rocket.stack.findIndex((c) => c.id === selectedId) : -1;
  return { entries, selected, isStack, stackIndex };
}

/** Árbol de componentes: una lista con foco itinerante y navegación con flechas. */
export function ComponentTree({ rocket, selectedId, issues, dispatch, onEdit }: TreeProps): JSX.Element {
  const listRef = useRef<HTMLUListElement>(null);
  const { entries } = selection(rocket, selectedId);

  /** Navegación con flechas: mueve el foco y la selección entre componentes. */
  const onKeyDown = (e: React.KeyboardEvent<HTMLUListElement>): void => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const buttons = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-id]') ?? []);
    if (buttons.length === 0) return;
    e.preventDefault();
    const current = buttons.findIndex((b) => b === document.activeElement);
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? buttons.length - 1 : Math.min(Math.max(current + (e.key === 'ArrowDown' ? 1 : -1), 0), buttons.length - 1);
    const target = buttons[next]!;
    target.focus();
    dispatch({ type: 'select', id: target.dataset['id'] });
  };

  return (
    <section aria-labelledby="components-title" className="region region--tree">
      <h2 id="components-title" className="sr-only">
        Componentes
      </h2>
      <ul className="component-list" ref={listRef} onKeyDown={onKeyDown} aria-label="Lista de componentes (usa las flechas para moverte)">
        <li className="component-list__root" aria-hidden="true">
          {rocket.name}
        </li>
        {/* Solo visual: el modelo aún no tiene etapas, el diseño es una sola. */}
        <li className="component-list__stage" aria-hidden="true">
          Etapa principal
        </li>
        {entries.map(({ component, parentId }) => {
          const own = issues.filter((i) => i.componentId === component.id);
          const errors = own.filter((i) => i.severity === 'error').length;
          const active = component.id === selectedId;
          const kind = KIND_LABEL[component.type];
          return (
            <li key={component.id} className={parentId === undefined ? 'component-list__stack' : 'component-list__child'}>
              <button
                type="button"
                data-id={component.id}
                className={`component-list__item${active ? ' is-active' : ''}`}
                aria-current={active ? 'true' : undefined}
                tabIndex={active || (selectedId === undefined && component.id === entries[0]!.component.id) ? 0 : -1}
                onClick={() => dispatch({ type: 'select', id: component.id })}
                onDoubleClick={onEdit}
              >
                <span className="component-list__name">{component.name}</span>
                {component.name !== kind && <span className="component-list__kind">({kind})</span>}
                {errors > 0 && (
                  <span className="badge badge--error" aria-label={`${errors} error${errors === 1 ? '' : 'es'}`}>
                    {errors}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Columna de botones entre el árbol y la paleta: mover, editar y borrar el componente seleccionado. */
export function ComponentActions({ rocket, selectedId, dispatch, onEdit }: ActionsProps): JSX.Element {
  const { selected, isStack, stackIndex } = selection(rocket, selectedId);
  return (
    <div className="actions" role="group" aria-label="Acciones sobre el componente seleccionado">
      <button type="button" disabled={!isStack || stackIndex <= 0} onClick={() => dispatch({ type: 'move', id: selectedId!, delta: -1 })}>
        Mover hacia arriba
      </button>
      <button type="button" disabled={!isStack || stackIndex < 0 || stackIndex >= rocket.stack.length - 1} onClick={() => dispatch({ type: 'move', id: selectedId!, delta: 1 })}>
        Mover hacia abajo
      </button>
      <button type="button" disabled={selected === undefined} onClick={onEdit}>
        Editar componente
      </button>
      <button type="button" className="danger" disabled={selected === undefined} onClick={() => dispatch({ type: 'remove', id: selectedId! })}>
        Borrar
      </button>
    </div>
  );
}

interface Tile {
  readonly label: string;
  readonly icon: IconName;
  /** Tipo que se añade; sin él, el componente aún no existe en el modelo y el botón queda atenuado. */
  readonly kind?: ComponentKind;
}

const PALETTE: ReadonlyArray<{ readonly title: string; readonly tiles: readonly Tile[] }> = [
  {
    title: 'Componentes de ensamblaje',
    tiles: [
      { label: 'Etapa', icon: 'stage' },
      { label: 'Propulsores laterales', icon: 'boosters' },
      { label: 'Módulos', icon: 'pods' },
    ],
  },
  {
    title: 'Componentes del fuselaje y aletas',
    tiles: [
      { label: 'Ojiva', icon: 'nose-cone', kind: 'nose-cone' },
      { label: 'Tubo de cuerpo', icon: 'body-tube', kind: 'body-tube' },
      { label: 'Transición', icon: 'transition', kind: 'transition' },
      { label: 'Aletas trapezoidales', icon: 'fin-trapezoid', kind: 'fin-set' },
      { label: 'Aletas elípticas', icon: 'fin-elliptical' },
      { label: 'Aletas de forma libre', icon: 'fin-freeform' },
      { label: 'Aletas tubulares', icon: 'tube-fins' },
      { label: 'Botón de riel', icon: 'rail-button' },
      { label: 'Soporte para guía', icon: 'launch-lug', kind: 'launch-lug' },
    ],
  },
  {
    title: 'Componentes internos',
    tiles: [
      { label: 'Tubo interior', icon: 'inner-tube', kind: 'inner-tube' },
      { label: 'Acoplador', icon: 'coupler' },
      { label: 'Anillo de centrado', icon: 'centering-ring', kind: 'centering-ring' },
      { label: 'Disco de enganche', icon: 'bulkhead', kind: 'bulkhead' },
      { label: 'Retén de motor', icon: 'engine-block', kind: 'engine-block' },
    ],
  },
  {
    title: 'Masas y recuperación',
    tiles: [
      { label: 'Paracaídas', icon: 'parachute', kind: 'parachute' },
      { label: 'Cordón elástico', icon: 'shock-cord', kind: 'shock-cord' },
      { label: 'Masa', icon: 'mass', kind: 'mass-component' },
    ],
  },
];

/**
 * «Añadir un nuevo componente»: baldosas con icono agrupadas como en OpenRocket. Las que este simulador aún no
 * modela se muestran atenuadas y no se pueden pulsar, para que se vea qué existe y qué falta.
 */
export function ComponentPalette({ dispatch }: { readonly dispatch: (a: Action) => void }): JSX.Element {
  return (
    <fieldset className="palette">
      <legend>Añadir un nuevo componente</legend>
      {PALETTE.map((g) => (
        <section key={g.title} className="palette__group" aria-label={g.title}>
          <h3 className="palette__title">{g.title}</h3>
          <div className="palette__tiles">
            {g.tiles.map((t) => (
              <button
                key={t.label}
                type="button"
                className="tile"
                disabled={t.kind === undefined}
                title={t.kind === undefined ? 'Este componente aún no está disponible en el simulador' : undefined}
                onClick={t.kind === undefined ? undefined : () => dispatch({ type: 'add', kind: t.kind! })}
              >
                <Icon name={t.icon} />
                <span>{t.label}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
      <p className="muted small">Los componentes de aletas, internos, masas y recuperación se añaden al tubo (o transición) seleccionado, o al primero.</p>
    </fieldset>
  );
}
